import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { UserPlus, UserCheck, FileDown, Check } from 'lucide-react';
import Topbar from '../components/Topbar';
import Sidebar from '../components/Sidebar'; // Importa el Sidebar
import { getCostRecords, updateCostRecord } from '../api'; // Import the API functions
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx'; // Add this import for Excel export

// Import Material-UI components for the client filter
import { Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText, OutlinedInput } from '@mui/material';

// Componente Costeo
const Costeo = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [costRecords, setCostRecords] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    line: '',
    client: '',
    account: ''  // Add account filter
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Costeo');
  const [searchValue, setSearchValue] = useState('');

  // Rename these state variables for clarity
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [allCostRecords, setAllCostRecords] = useState([]); // Store all unfiltered records

  // Fetch cost records from the API without filtering
  const fetchCostRecords = async () => {
    try {
      setLoading(true);
      // Only use date filters for API call if needed
      const baseFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate
      };
      
      const data = await getCostRecords(baseFilters);
      
      // Group by shipment (viaje)
      const groupedByViaje = {};
      
      // Collect unique accounts for the filter dropdown
      const accountsSet = new Set();

      data.forEach(record => {
        // Add account to unique accounts set if it exists
        if (record.cuenta) {
          accountsSet.add(record.cuenta);
        }

        if (!groupedByViaje[record.viaje]) {
          groupedByViaje[record.viaje] = {
            id: record.id,
            viaje: record.viaje,
            cliente: record.cliente,
            cuenta: record.cuenta || record.client_account || '', // Add account property
            linea_transporte: record.linea_transporte,
            validado: record.validado,
            // Store origin, destination and vehicle type 
            origen: record.origen || '',
            destino: record.destino || '',
            tipo_vehiculo: record.tipo_vehiculo || '',
            // Initialize concept fields with 0
            flete: 0,
            maniobra: 0,
            estadias: 0,
            devoluciones: 0,
            otros: 0,
            // Keep track of all record IDs for this viaje
            recordIds: []
          };
        } else {
          // Update fields if not already set
          if (record.origen && !groupedByViaje[record.viaje].origen) {
            groupedByViaje[record.viaje].origen = record.origen;
          }
          if (record.destino && !groupedByViaje[record.viaje].destino) {
            groupedByViaje[record.viaje].destino = record.destino;
          }
          if (record.tipo_vehiculo && !groupedByViaje[record.viaje].tipo_vehiculo) {
            groupedByViaje[record.viaje].tipo_vehiculo = record.tipo_vehiculo;
          }
          if (record.cuenta && !groupedByViaje[record.viaje].cuenta) {
            groupedByViaje[record.viaje].cuenta = record.cuenta;
          }
        }
        
        // Add cost to the appropriate concept field
        const concept = record.concepto?.toUpperCase() || '';
        
        if (concept === 'FLETE') {
          groupedByViaje[record.viaje].flete += record.costo || 0;
        } else if (concept === 'MANIOBRA') {
          groupedByViaje[record.viaje].maniobra += record.costo || 0;
        } else if (concept === 'ESTADIAS' || concept === 'ESTADIA') {
          groupedByViaje[record.viaje].estadias += record.costo || 0;
        } else if (concept === 'DEVOLUCION' || concept === 'DEVOLUCIONES') {
          groupedByViaje[record.viaje].devoluciones += record.costo || 0;
        } else if (concept) {
          groupedByViaje[record.viaje].otros += record.costo || 0;
        }
        
        // Add record ID to the array
        groupedByViaje[record.viaje].recordIds.push(record.id);
        
        // If any record is validated, mark the whole shipment as validated
        if (record.validado) {
          groupedByViaje[record.viaje].validado = true;
        }
      });
      
      // Convert the grouped object back to an array
      const groupedRecords = Object.values(groupedByViaje);
      
      // Store all records before filtering
      setAllCostRecords(groupedRecords);
      
      // Update available accounts list after fetching data
      const accountsArray = Array.from(accountsSet).sort();
      setAvailableAccounts(accountsArray);

      // Initialize selected accounts if empty
      if (selectedAccounts.length === 0) {
        setSelectedAccounts(accountsArray);
      }
      
      // Apply initial filtering
      applyFilters(groupedRecords);
      
      setLoading(false);
    } catch (err) {
      console.error("Error fetching cost records:", err);
      setError("Error al cargar los registros de costo");
      setLoading(false);
    }
  };

  // New function to apply filters locally
  const applyFilters = (records = allCostRecords) => {
    // Start with all records
    let filteredRecords = [...records];
    
    // Apply account filter
    if (selectedAccounts.length > 0 && selectedAccounts.length < availableAccounts.length) {
      filteredRecords = filteredRecords.filter(record => 
        selectedAccounts.includes(record.cuenta)
      );
    }
    
    // Apply client filter
    if (filters.client) {
      filteredRecords = filteredRecords.filter(record => 
        record.cliente && record.cliente.toLowerCase().includes(filters.client.toLowerCase())
      );
    }
    
    // Apply line filter
    if (filters.line) {
      filteredRecords = filteredRecords.filter(record => 
        record.linea_transporte && record.linea_transporte.toLowerCase().includes(filters.line.toLowerCase())
      );
    }
    
    // Update the displayed records
    setCostRecords(filteredRecords);
  };

  useEffect(() => {
    fetchCostRecords();
  }, []);

  // Rename this to handleAccountChange for clarity
  const handleAccountChange = (event) => {
    const {
      target: { value },
    } = event;
    
    if (value.includes('all')) {
      if (selectedAccounts.length !== availableAccounts.length) {
        setSelectedAccounts([...availableAccounts]);
      } else {
        setSelectedAccounts([]);
      }
    } 
    else {
      setSelectedAccounts(typeof value === 'string' ? value.split(',') : value);
    }
  };

  // Fix the filter handling by ensuring we're using the updated filter
  const handleFilter = () => {
    // Store the account filter in the filters state
    const accountFilter = selectedAccounts.length === availableAccounts.length ? '' : selectedAccounts.join(',');
    
    // Update the filters state
    const updatedFilters = { 
      ...filters,
      account: accountFilter
    };
    setFilters(updatedFilters);
    
    // Apply filters locally without making a new API call
    applyFilters();
  };

  const handleValidation = async (recordIds) => {
    try {
      // Update all record IDs associated with this viaje
      for (const recordId of recordIds) {
        await updateCostRecord(recordId, { validado: true });
      }
      
      // Update local state
      setCostRecords(prevRecords => 
        prevRecords.map(record => 
          record.recordIds.some(id => recordIds.includes(id))
            ? { ...record, validado: true }
            : record
        )
      );
      
      toast.success("Registro validado exitosamente");
    } catch (err) {
      console.error("Error validating record:", err);
      setError("Error al validar el registro");
      toast.error("Error al validar el registro");
    }
  };

  const handleAddToSabana = async (recordIds) => {
    try {
      // Implement the logic to add to sabana
      // This would typically involve calling an API endpoint
      console.log("Adding to sabana:", recordIds);
      
      toast.success("Registros agregados a la sábana correctamente");
    } catch (err) {
      console.error("Error adding to sabana:", err);
      setError("Error al agregar a la sábana");
      toast.error("Error al agregar a la sábana");
    }
  };
  
  const handleSaveChanges = async () => {
    try {
      // Implement save changes logic
      toast.success("Cambios guardados correctamente");
    } catch (err) {
      console.error("Error saving changes:", err);
      setError("Error al guardar cambios");
      toast.error("Error al guardar cambios");
    }
  };

  // Function to prepare data for Excel export - updated to use filtered data
  const prepareDataForExport = () => {
    try {
      // Use the already filtered data from costRecords state
      const filteredData = costRecords;
      
      if (filteredData.length === 0) {
        return { data: [], concepts: [] };
      }

      // Identify all unique concepts across records
      const allConcepts = new Set(['FLETE', 'MANIOBRA', 'ESTADIAS', 'DEVOLUCION', 'DEVOLUCIONES', 'OTROS']);
      
      // Create a sorted array of concepts with the main ones first
      const mainConcepts = ['FLETE', 'MANIOBRA', 'ESTADIAS', 'DEVOLUCION'];
      const sortedConcepts = [...mainConcepts];
      allConcepts.forEach(concept => {
        if (!mainConcepts.includes(concept)) {
          sortedConcepts.push(concept);
        }
      });
      
      // Convert the filtered data to the format needed for Excel
      const exportData = [];
      
      // For each cost record, get all associated original records to find origin, destination, etc.
      for (const item of filteredData) {
        // Find all original records associated with this viaje
        const originalRecords = allCostRecords.find(record => record.viaje === item.viaje);
        
        // Create a new object for Excel using all available data
        const excelRow = {
          ID: item.id || '',
          Viaje: item.viaje || '',
          Cliente: item.cuenta || '',
          Cliente_Final: item.cliente || '',
          // Get the details from the first available record that contains them
          Origen: item.origen || '',
          Destino: item.destino || '',
          Linea_Transporte: item.linea_transporte || '',
          Tipo_Vehiculo: item.tipo_vehiculo || '',
          Validado: item.validado ? 'Sí' : 'No',
          FLETE: item.flete || 0,
          MANIOBRA: item.maniobra || 0,
          ESTADIAS: item.estadias || 0,
          DEVOLUCION: item.devoluciones || 0, 
          OTROS: item.otros || 0,
          TOTAL: (item.flete || 0) + 
                 (item.maniobra || 0) + 
                 (item.estadias || 0) + 
                 (item.devoluciones || 0) + 
                 (item.otros || 0)
        };
        
        // Ensure no empty fields by defaulting to 'N/A' for text fields
        for (const key in excelRow) {
          if (excelRow[key] === '' || excelRow[key] === null || excelRow[key] === undefined) {
            // For concept columns, default to 0
            if (sortedConcepts.includes(key)) {
              excelRow[key] = 0;
            } else if (key !== 'TOTAL') {
              // For other text fields, default to 'N/A', but not for TOTAL
              excelRow[key] = 'N/A';
            }
          }
        }
        
        exportData.push(excelRow);
      }
      
      return {
        data: exportData,
        concepts: sortedConcepts
      };
    } catch (error) {
      console.error("Error preparing export data:", error);
      toast.error("Error al preparar datos para exportar");
      return { data: [], concepts: [] };
    }
  };

  // Function to export data to Excel - updated to use the new prepareDataForExport
  const exportToExcel = () => {
    try {
      const { data, concepts } = prepareDataForExport();
      
      if (data.length === 0) {
        toast.error("No hay datos para exportar");
        return;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Set column widths
      const columnWidths = [
        { wch: 10 },  // ID
        { wch: 15 },  // Viaje
        { wch: 25 },  // Cliente
        { wch: 25 },  // Cliente_Final
        { wch: 20 },  // Origen
        { wch: 20 },  // Destino
        { wch: 20 },  // Linea_Transporte
        { wch: 15 },  // Tipo_Vehiculo
        { wch: 10 },  // Validado
      ];
      
      // Add width for each concept column
      concepts.forEach(() => {
        columnWidths.push({ wch: 15 });
      });
      
      // Add width for total column
      columnWidths.push({ wch: 15 });
      
      worksheet['!cols'] = columnWidths;
      
      // Format currency for amount columns
      const amountColumns = [...concepts, 'TOTAL'];
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      
      for (let C = 0; C <= range.e.c; C++) {
        const header = XLSX.utils.encode_col(C) + "1";
        const headerValue = worksheet[header]?.v;
        
        if (headerValue && amountColumns.includes(headerValue)) {
          for (let R = 2; R <= range.e.r + 1; R++) {
            const cell = XLSX.utils.encode_cell({c: C, r: R-1});
            if (worksheet[cell]) {
              // Format as currency
              worksheet[cell].z = '"$"#,##0.00';
            }
          }
        }
      }
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Costos");
      
      // Generate Excel file and trigger download with date in the filename
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(workbook, `Costos_${dateStr}.xlsx`);
      
      toast.success("Exportado a Excel exitosamente");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Error al exportar a Excel");
    }
  };

  const handleExportToExcel = () => {
    exportToExcel();
  };

  const handleLogout = () => {
    // Implement logout functionality
    console.log("Logout");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="flex">
      <Sidebar
        isOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        onLogout={handleLogout}
      />

      <div
        className={`transition-all duration-300 flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen p-6`}
      >
        <Topbar 
          title="Costeo" 
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <input
                type="date"
                className="border p-2 rounded"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
              />
              <input
                type="date"
                className="border p-2 rounded"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
              />
              {/* Material-UI Select for Cliente (account) filter - updated to use account variables */}
              <FormControl sx={{ width: '100%' }} size="small">
                <InputLabel id="account-select-label">Cliente</InputLabel>
                <Select
                  labelId="account-select-label"
                  id="account-select"
                  multiple
                  value={selectedAccounts}
                  onChange={handleAccountChange}
                  input={<OutlinedInput label="Cliente" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return "Todos los clientes";
                    }
                    return selected.length === 1 
                      ? selected[0]
                      : `${selected.length} cliente(s) seleccionado(s)`;
                  }}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiSelect-select': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 4.5 + 8,
                        width: 250,
                      },
                    },
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox 
                      checked={selectedAccounts.length === availableAccounts.length}
                      indeterminate={selectedAccounts.length > 0 && selectedAccounts.length < availableAccounts.length} 
                    />
                    <ListItemText primary={selectedAccounts.length === availableAccounts.length ? "Deseleccionar todos" : "Seleccionar todos"} />
                  </MenuItem>
                  {availableAccounts.map((account) => (
                    <MenuItem key={account} value={account}>
                      <Checkbox checked={selectedAccounts.includes(account)} />
                      <ListItemText primary={account} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Regular input for Cliente Final */}
              <input
                type="text"
                placeholder="Cliente Final"
                className="border p-2 rounded"
                value={filters.client}
                onChange={(e) =>
                  setFilters({ ...filters, client: e.target.value })
                }
              />
              <input
                type="text"
                placeholder="Línea transportista"
                className="border p-2 rounded"
                value={filters.line}
                onChange={(e) =>
                  setFilters({ ...filters, line: e.target.value })
                }
              />
            </div>
            <Button className="mt-4" onClick={handleFilter}>
              Aplicar Filtros
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lista de Costos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div>Cargando...</div>
            ) : error ? (
              <div className="text-red-500">{error}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Viaje</TableHead>
                    <TableHead>Cliente Final</TableHead>
                    <TableHead>Línea Transportista</TableHead>
                    <TableHead>Flete</TableHead>
                    <TableHead>Maniobra</TableHead>
                    <TableHead>Estadías</TableHead>
                    <TableHead>Devoluciones</TableHead>
                    <TableHead>Otros</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Validado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costRecords.map((item) => (
                    <TableRow key={item.viaje}>
                      <TableCell>{item.cuenta}</TableCell>
                      <TableCell>{item.viaje}</TableCell>
                      <TableCell>{item.cliente}</TableCell>
                      <TableCell>{item.linea_transporte}</TableCell>
                      <TableCell>{formatCurrency(item.flete)}</TableCell>
                      <TableCell>{formatCurrency(item.maniobra)}</TableCell>
                      <TableCell>{formatCurrency(item.estadias)}</TableCell>
                      <TableCell>{formatCurrency(item.devoluciones)}</TableCell>
                      <TableCell>{formatCurrency(item.otros)}</TableCell>
                      <TableCell className="font-bold">
                        {formatCurrency(
                          item.flete + item.maniobra + item.estadias + 
                          item.devoluciones + item.otros
                        )}
                      </TableCell>
                      <TableCell>{item.validado ? 'Sí' : 'No'}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <div className="group relative">
                            <Button
                              className={`p-2 rounded-full ${item.validado ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-500 hover:bg-gray-600'}`}
                              onClick={() => handleValidation(item.recordIds)}
                              disabled={item.validado}
                            >
                              <UserCheck className="text-white h-6 w-6" />
                            </Button>
                            <span className="absolute left-1/2 transform -translate-x-1/2 bottom-8 text-xs text-white bg-gray-800 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                              Validar
                            </span>
                          </div>
                          <div className="group relative">
                            <Button
                              className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
                              onClick={() => handleAddToSabana(item.recordIds)}
                            >
                              <UserPlus className="h-6 w-6" />
                            </Button>
                            <span className="absolute left-1/2 transform -translate-x-1/2 bottom-8 text-xs text-white bg-gray-800 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                              Agregar a Sábana
                            </span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="flex justify-between mt-4">
              <Button onClick={handleSaveChanges}>Guardar Cambios</Button>
              <Button 
                onClick={handleExportToExcel} 
                className="flex items-center gap-2"
                disabled={loading || costRecords.length === 0}
              >
                <FileDown className="h-5 w-5" />
                <span>Exportar a Excel</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Costeo;